import "./UserChat.css";
import { useEffect, useState, useContext, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SocketContext } from "../../contexts.js/SocketContext";
import Cookies from "js-cookie";
import UserChatWindow from "./UserChatWindow";
import { getChatHistoryApi } from "../../api/chatApi";
import { setRoomHasUnreadMsg } from "../../redux/userSlice";
import { updateStorageUnreadMessages } from "../../utilities/storageManager";
import ReactCurvedText from "react-curved-text";

export default function UserChat(props) {
  // complete user chat component with window + fixed icon at bottom of screen that is always visible to user

  const dispatch = useDispatch();
  const { socket } = useContext(SocketContext);
  const { firstName, id, unreadMessages } = useSelector((state) => state.user);
  const sessionIdFromCookies = Cookies.get("sessionId");

  const welcomeMsg = {
    admin: "Welcome to our site! How can we help you today?",
  };
  const chatEndsMsgRef = useRef({
    admin:
      "It was a pleasure assisting you! If you have any further inquiries or need help in the future, please don't hesitate to ask.",
  });
  const noAdminMsgRef = useRef({
    admin: "We are currently not online. Please try again later.",
  });

  const initialChatHistory = [welcomeMsg];
  const user = useSelector((state) => state.user);
  const [chatHistory, setChatHistory] = useState([...initialChatHistory]); //[{client: "msg"}, {admin: "msg"}, ...]
  const [newMessagesQueue, setNewMessagesQueue] = useState([]);
  const [sessionId, setSessionId] = useState(sessionIdFromCookies);
  const [showRestartChat, setShowRestartChat] = useState(false);
  const [adminIsTyping, setAdminIsTyping] = useState(false);
  const [adminAvailable, setAdminAvailable] = useState(false);
  const [updateNewMsgAlert, setUpdateNewMsgAlert] = useState(false);
  const [chatErrorMsg, setChatErrorMsg] = useState("");

  useEffect(() => {
    if (socket) {
      // event listeners

      socket.on("connect", () => {
        setChatErrorMsg("");
          socket.emit("registerClient", sessionId);
      })

      socket.on("connect_error", () => {
        setChatErrorMsg("We're having trouble connecting to the chat server. Please wait while we try to reconnect...")
      })

      socket.on("adminsAvailabilityChange", (isAvailable) => {
        // at least 1 admin available || none available
        setAdminAvailable(isAvailable);
      });

      socket.on("noAdminAvailable", (message) => {
        setNewMessagesQueue((prevQueue) => [
          ...prevQueue,
          noAdminMsgRef.current,
        ]);
      });

      socket.on("createNewSession", (sessionId) => {
        Cookies.set("sessionId", sessionId, { expires: 1, path: "/" }); // session cookie (expires when close window, not on refresh)
        setSessionId(sessionId);
      });

      socket.on("newAdminMessage", (message) => {
        setNewMessagesQueue((prevQueue) => [...prevQueue, message]);
      });

      socket.on("chatEnds", () => {
        setNewMessagesQueue((prevQueue) => [
          ...prevQueue,
          chatEndsMsgRef.current,
        ]);
        setShowRestartChat(true);
        setAdminIsTyping(false);
      });

      socket.on("partnerIsTyping", (data) => {
        setAdminIsTyping(data?.isTyping);
      });

      socket.on("chatsActiveStatus", (isActive) => {
        setShowRestartChat(!isActive);
      });


    } 
    return () => {
      // clean up event listeners before unmount/next execution of useEffect (or will have multiple listeners for an event)
      if (socket) {
        socket.off("adminsAvailabilityChange");
        socket.off("noAdminAvailable");
        socket.off("createNewSession");
        socket.off("chatEnds");
        socket.off("chatsActiveStatus");
        socket.off("newAdminMessage");
        socket.off("partnerIsTyping");
        socket.off("connect_error"); 
        socket.off("connect");
      }
    };
  }, [socket, sessionId]);

  useEffect(() => {
    if (newMessagesQueue.length > 0) {
      // update chat history & new message indicator
      setChatHistory((prevHistory) => {
        const updatedHistory = [...prevHistory];
        newMessagesQueue.forEach((message) => {
          updatedHistory.push(message);
          // if admin msg, set indicator to true
          if (message?.admin && message.admin !== noAdminMsgRef.current.admin) {
            setUpdateNewMsgAlert(true); 
          }
        });
        return updatedHistory;
      });
      setNewMessagesQueue([]);
    }
  }, [
    newMessagesQueue,
  ]);



  useEffect(() => {
    if(updateNewMsgAlert){
      if(!unreadMessages[sessionId]){
        updateStorageUnreadMessages(
          { hasNewMessage: true, sessionId },
          user.stayLoggedIn
          );
          dispatch(setRoomHasUnreadMsg({ hasNewMessage: true, sessionId }));
        }
      }
      setUpdateNewMsgAlert(false);
  }, [updateNewMsgAlert, sessionId, user.stayLoggedIn, dispatch, unreadMessages])



  const submitChatMsg = (newMsg) => {
    let messageData = {
      user: newMsg,
      createdAt: new Date(),
    };
    // update chat history with new user message
    setChatHistory((prevHistory) => [...prevHistory, messageData]);
    socket.emit("newClientMessage", {
      message: messageData,
      sessionId,
      userName: firstName || "User",
      userId: id,
    });
  };

  const handleStartNewChat = () => {
    setChatHistory([...initialChatHistory]);
    Cookies.set("sessionId", null);
    setSessionId(null);
    setShowRestartChat(false);
  };

  const getChatHistory = useCallback(async () => {
    try {
      if (!sessionId) {
        return;
      }
      const abortController = new AbortController();
      const data = await getChatHistoryApi(sessionId, abortController.signal);
      if (data?.chatHistory) {
        setChatHistory(data.chatHistory);
        setShowRestartChat(data?.closed);
      }
      if (socket) {
        socket.emit("checkChatsActiveStatus", sessionId);
      }
    } catch (error) {
      console.log("Could not retrieve chat history.", error);
    }
  }, [sessionId, socket]);

  const handleTyping = (isTyping) => {
    socket.emit("isTyping", {
      sessionId,
      isTyping,
    });
  };

  return (

    <div className="chat-icon-container">
      {/* CHAT ICON + We're here text*/}
      {adminAvailable && (
        <ReactCurvedText
          width={185}
          height={294}
          cx={100}
          cy={150}
          rx={80}
          ry={80}
          startOffset={80}
          reversed={false}
          text="We're online!"
          textProps={{
            style: { fontSize: 16, fill: "#1a4ef6", fontWeight: "500" },
          }}
          textPathProps={null}
          tspanProps={null}
          ellipseProps={null}
          svgProps={{
            style: {
              position: "fixed",
              bottom: "calc(var(--chat-btn-bottom-margin) - var(--chat-btn-height) - 32px)",
              right: "-33px", //38
            },
          }}
        />
      )}

      <input
        type="checkbox"
        id="chat-status"
        checked={props?.chatExpanded}
        onClick={() => props?.toggleChatWindow()}
        onChange={() => {}}
      />
      {/* click will toggle the chatStatus */}
      <label className="chat-btn" htmlFor="chat-status">
        <i className="bi bi-chat-dots chat-icon"></i>
        {/* new message indicator light */}
        {unreadMessages[sessionId] && (
          <span className="position-absolute top-1 start-0 translate-middle p-2 bg-danger border border-light rounded-circle"></span>
        )}
        <i className="bi bi-x-circle chat-x-icon"></i>
      </label>

      {props?.chatExpanded && (
        <UserChatWindow
          submitChatMsg={submitChatMsg}
          chatHistory={chatHistory}
          showRestartChat={showRestartChat}
          handleStartNewChat={handleStartNewChat}
          sessionId={sessionId}
          getChatHistory={getChatHistory}
          initialChatHistory={[...initialChatHistory]}
          partnerIsTyping={adminIsTyping}
          handleTyping={handleTyping}
          chatErrorMsg={chatErrorMsg}
        />
      )}
    </div>
  );
}
